import { type CSSProperties } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

export const PRIYA_CURSOR_TRAVEL_MS = 1050;

type JsmPlaygroundPriyaCursorProps = {
	x: number;
	y: number;
	isVisible: boolean;
	isPressed: boolean;
	isTransitionEnabled: boolean;
	hasUserTakenOver: boolean;
};

export const JsmPlaygroundPriyaCursor = ({
	x,
	y,
	isVisible,
	isPressed,
	isTransitionEnabled,
	hasUserTakenOver,
}: JsmPlaygroundPriyaCursorProps) => {
	const isShown = isVisible && !hasUserTakenOver;

	return (
		<div
			aria-hidden="true"
			css={cursorStyles}
			data-visible={isShown}
			data-testid="jsm-playground-priya-cursor"
			style={
				{
					'--priya-cursor-opacity': isShown ? 1 : 0,
					'--priya-cursor-scale': isShown ? 1 : 0.92,
					'--priya-cursor-transition': isTransitionEnabled
						? `transform ${PRIYA_CURSOR_TRAVEL_MS}ms cubic-bezier(0.33, 0, 0.2, 1)`
						: 'transform 0ms',
					'--priya-cursor-x': `${x}px`,
					'--priya-cursor-y': `${y}px`,
				} as CSSProperties
			}
		>
			<svg
				css={[cursorArrowStyles, isPressed && cursorArrowPressedStyles]}
				fill="none"
				height="24"
				viewBox="0 0 23 23"
				width="24"
			>
				<path
					d="M7.735 22.62 0 0l22.585 7.771-9.899 4.951-4.951 9.898Z"
					fill="currentColor"
					stroke="white"
					strokeWidth="1.5"
				/>
			</svg>
			<span css={cursorLabelStyles}>Priya</span>
		</div>
	);
};

const cursorStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	zIndex: 700,
	pointerEvents: 'none',
	opacity: 'var(--priya-cursor-opacity)',
	transform:
		'translate3d(var(--priya-cursor-x), var(--priya-cursor-y), 0) scale(var(--priya-cursor-scale))',
	transformOrigin: 'top left',
	transition: 'var(--priya-cursor-transition), opacity 400ms cubic-bezier(0.33, 0, 0.2, 1)',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const cursorArrowStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	color: token('color.background.accent.blue.bolder'),
	filter: `drop-shadow(0 2px 6px ${token('color.blanket')})`,
	transform: 'scale(1)',
	transformOrigin: 'top left',
	transition: 'transform 200ms cubic-bezier(0.33, 0, 0.2, 1)',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const cursorArrowPressedStyles = css({
	transform: 'scale(0.9)',
});

const cursorLabelStyles = css({
	position: 'absolute',
	insetBlockStart: token('space.150'),
	insetInlineStart: token('space.300'),
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	paddingBlock: token('space.025'),
	paddingInline: token('space.150'),
	borderRadius: token('radius.full'),
	backgroundColor: token('color.background.accent.blue.bolder'),
	color: token('color.text.inverse'),
	font: token('font.heading.medium'),
	whiteSpace: 'nowrap',
	boxShadow: token('elevation.shadow.overlay'),
});
